// ©2024 Austin App House. All rights reserved.
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { SubmissionError, submit } from 'redux-form';
import { toggleModal } from '../../storage/actions/modalsActions';
import Notification from '../notification';
import CreateCustomFieldReduxForm from '../layout/reduxForm/CreateCustomField';
import validate from '../../utils/reduxForm/validate/createCustomField';
import { createCustomField } from '../../storage/actions/workOrdersActions';
import getCustomFieldsList from '../../utils/api/get/getCoustomFieldsList';
import { dashboardSearchable, dashboardSelectClassNames } from '../../utils/customSelectStyle';
import CustomSelect from '../layout/CustomSelect';
import DashboardModalShell from './DashboardModalShell';

const CreateOrAddCustomFieldToWorkOrder = ({
  modalState,
  toggleModal,
  submit,
}) => {
  const [customFields, setCustomFields] = useState([]);
  const [selectedCustomField, setSelectedCustomField] = useState();

  const createCustomFieldsOptionsForSelect = (list) => (
    list.map((customField) => ({
      value: customField._id,
      label: customField.name,
    }))
  );

  // eslint-disable-next-line consistent-return
  const fetchCustomFields = async () => {
    const newCustomFields = await getCustomFieldsList();
    if (!(modalState.type?.input.value?.length > 0)) {
      return setCustomFields(
        createCustomFieldsOptionsForSelect(newCustomFields),
      );
    }
    const selectedCustomFieldsIds = modalState.type?.input.value.map((item) => (
      item._id || item.value
    ));
    setCustomFields(
      createCustomFieldsOptionsForSelect(
        newCustomFields.filter((customField) => !selectedCustomFieldsIds.includes(
          customField._id,
        )),
      ),
    );
  };

  useEffect(() => {
    fetchCustomFields();
  }, []);

  const closeModal = () => {
    toggleModal(false, 'createOrAddCustomFieldToWorkOrder');
  };

  const getCustomFieldInfo = async (name, dispatch) => {
    if (selectedCustomField?.value) {
      return ({
        customFieldId: selectedCustomField.value,
        name: selectedCustomField.label,
      });
    }
    const result = await dispatch(createCustomField(name));
    if (!result.success) throw new SubmissionError({ _error: result.message });
    return ({
      customFieldId: result?.payload?.newDocument?._id,
      name: result?.payload?.newDocument?.name,
    });
  };

  const onSubmit = async (values, dispatch, props) => {
    const validationErrors = validate(values);
    if (Object.keys(validationErrors).length) throw new SubmissionError(validationErrors);
    if (!props.dirty) return Notification('warning', { message: 'None changes found!' });
    const {
      name,
      value,
      type,
    } = values;
    const customFieldInfo = await getCustomFieldInfo(name, dispatch);
    addCustomFieldToWorkOrder({
      customFieldId: customFieldInfo.customFieldId,
      name: customFieldInfo.name,
      value,
      type: type.value,
    });
    return {};
  };

  const handlerSubmit = (event) => {
    event.preventDefault();
    submit('createCustomFieldReduxForm');
  };

  const addCustomFieldToWorkOrder = (customFiled) => {
    if (!(modalState.type?.input.value?.length > 0)) {
      modalState.type.input.onChange([customFiled]);
    } else {
      modalState.type.input.onChange([...modalState.type?.input.value, customFiled]);
    }
    closeModal();
  };

  return (
    <DashboardModalShell
      isOpen={!!modalState?.isOpen}
      onClose={closeModal}
      title="Add Custom Field"
      titleId="add-custom-field-title"
      className="dashboard-modal--custom-field"
      bodyClassName="wurqly-modal-scroll dashboard-modal-form-scroll"
      footer={(
        <>
          <button
            type="button"
            className="dashboard-btn dashboard-btn--primary"
            onClick={handlerSubmit}
          >
            Save
          </button>
          <button
            type="button"
            className="dashboard-btn dashboard-btn--secondary"
            onClick={closeModal}
          >
            Cancel
          </button>
        </>
      )}
    >
      <div className="field_col">
        <label className="field_name" htmlFor="custom_field_select">
          Select Custom Field
        </label>
        <div className="field_block dashboard-select-wrap">
          <CustomSelect
            inputId="custom_field_select"
            isClearable
            isSearchable
            options={customFields}
            value={selectedCustomField}
            styles={dashboardSearchable}
            classNames={dashboardSelectClassNames}
            onChange={setSelectedCustomField}
            placeholder="Select Custom Field"
          />
        </div>
      </div>

      <div className="separate">OR</div>

      <CreateCustomFieldReduxForm
        enableReinitialize
        onSubmit={onSubmit}
        disabled={!!selectedCustomField?.value}
        initialValues={selectedCustomField?.value ? { name: selectedCustomField.label } : {}}
      />
    </DashboardModalShell>
  );
};

const mapDispatchToProps = {
  toggleModal,
  submit,
};

const mapStateToProps = (state) => ({
  modalState: state.modalState.createOrAddCustomFieldToWorkOrder,
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateOrAddCustomFieldToWorkOrder);
